import { useMemo, useState } from "react"
import { BinaryOperator, Filter } from "@cubejs-client/core"

import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"
import { renderSourceIcon } from "~/ui-rtk/components/ui/charts/TableVisualization/components/Cell/utils"
import {
  TableVisualizationColumn,
  TSource,
} from "~/ui-rtk/constants/table-visualization-types"
import {
  Card,
  DashboardBreadcrumbs,
  RichTooltip,
} from "~/ui-rtk/components/ui/common"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import {
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import { DRAWER_TYPE, TDrawerConfig } from "~/ui-rtk/constants/drawer"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDrawer from "~/ui-rtk/hooks/drawer"
import useSources from "~/ui-rtk/hooks/sources"
import {
  BRAND_MEDIA_SOCIAL_POSTS_TABLES,
  type TBrandMediaSocialPostsTableId,
  type TBrandMediaSocialPostsTable,
} from "~/ui-rtk/pages/BrandMedia/OrganicSocialPosts/connect"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import BrandMediaSocialPostsSortedSection from "./components/SortedSection/SortedSection"
import { Link } from "react-router-dom"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import {
  type TLastUpdateDateProps,
  useLastUpdateDate,
} from "~/ui-rtk/hooks/cube"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { URL_WIDGET_PARAM_KEY } from "~/ui-rtk/constants/url-params"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { convertToCubeFilters } from "~/ui-rtk/utils/cube"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import MarathonDataLogo from "~/ui-rtk/components/ui/svg/Logo"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import HighlightToggle from "~/ui-rtk/components/ui/controls/HighlightToggle/HighlightToggle"

// Items map is keyed by the caller's own table id enum (e.g. ALL_POSTS,
// FACEBOOK on OrganicSocialPosts; ALL on AdBlaster's Ads tab). Loosened to
// `string` so the dashboard can be reused for non-social-post Cube widgets.
type TBrandMediaSocialPostsDashboardCharts = Record<
  string,
  TBrandMediaSocialPostsTable
>

type TSocialPostsDashboardProps = {
  slug: string
  title: string
  parent: {
    slug: string
    title: string
  }
  items: TBrandMediaSocialPostsDashboardCharts
  lastDateChipProps: TLastUpdateDateProps
  /**
   * Hide the top breadcrumb when this dashboard is embedded inside another
   * page (e.g. AdBlaster's Social Posts tab) where the page chrome already
   * provides the navigation context. Optional — defaults to showing the
   * breadcrumb, matching the original BrandMedia/OrganicSocialPosts behavior.
   */
  hideBreadcrumb?: boolean
  /**
   * Hide the FB / IG / TikTok / YouTube channel button strip. Used by callers
   * that want a single combined view (e.g. AdBlaster's Social Posts tab
   * scopes to IG + FB only and doesn't want users to switch). Defaults to
   * showing the strip.
   */
  hideChannelTabs?: boolean
  /**
   * Extra Cube filters AND-merged into the active widget's `cubeFilters`.
   * Use this to scope the query without authoring a new widget config —
   * e.g. `media_channel IN ['instagram', 'facebook']` for AdBlaster.
   */
  extraCubeFilters?: Filter[]
  /**
   * Columns prepended to the table in order — typically a checkbox for row
   * selection (and optionally a preview thumbnail when the widget config
   * doesn't already expose one). The render function receives the row +
   * merged renderConfig, so callers pass their selection state through
   * `renderConfigOverride`. Leading columns are always visible (not gated by
   * the column-picker), so callers can rely on them being present.
   */
  leadingColumns?: TableVisualizationColumn[]
  /**
   * Extra entries spread into the renderConfig that's threaded into each
   * column's render function. Used together with `leadingColumn` to pipe
   * selection state + callbacks into the checkbox column.
   */
  renderConfigOverride?: Record<string, unknown>
  /**
   * Drawer component to open on row click. Defaults to "PostDetails" for
   * the OrganicSocialPosts use case. Pass "AdDetails" when mounting the
   * dashboard against a paid-media ads widget.
   */
  drawerComponent?: string
  /**
   * Override the default sort column. Falls back to the widget's `sortBy`
   * when omitted. Should be the same key shape the widget's columns use
   * (prefixed for social posts, unprefixed for ads — match the widget's
   * own column config so TanStack's sort engine resolves to the right
   * column id).
   */
  defaultSortBy?: string
  /**
   * When true, Cube ORDER BY clauses prefix the column key with the
   * widget's `queryPrefix`. Required for widgets whose columnConfig uses
   * unprefixed keys (e.g. the ads widget's `brand_value_agg`); leave off
   * for fully-prefixed widgets (e.g. organic-posts'
   * `company_media_metrics_organic_posts.brand_value_agg`).
   */
  useOrderQueryPrefix?: boolean
  /**
   * Override row-click behavior. When set, this fires on row click and the
   * default drawer-opening path is bypassed. Used by AdBlaster's tabs to
   * toggle selection on click rather than open AdDetails / PostDetails.
   */
  onRowClick?: (row: Record<string, unknown>) => void
}

const BEST_WORST_ENABLED = false

const dayjs = useDate()
const maxDateRestriction = dayjs().toDate()

export const BrandMediaSocialPostsDashboard: React.FC<
  TSocialPostsDashboardProps
> = ({
  items,
  slug,
  title,
  parent,
  lastDateChipProps,
  hideBreadcrumb,
  hideChannelTabs,
  extraCubeFilters,
  leadingColumns,
  renderConfigOverride,
  drawerComponent = "PostDetails",
  defaultSortBy,
  useOrderQueryPrefix,
  onRowClick,
}) => {
  const currentCompany = useCurrentCompany()
  const { params, setParams } = useURLSearchParams()
  const { sources } = useSources()
  const { setPeriod } = usePeriod()
  const { openDrawer } = useDrawer()
  const { dateMetric, filterMetrics } = lastDateChipProps
  const { amazonEnabled, toggleAmazon, ...depVarConfig } = useDepVar()
  const [highlight, setHighlight] = useState(true)

  // CSV Export functionality

  const { lastDate, isLoading: isLastUpdateDateLoading } = useLastUpdateDate({
    dateMetric,
    filterMetrics,
  })
  const { dateRange, compareRange } = useMinMaxDateRange(
    undefined,
    lastDate ? new Date(lastDate) : maxDateRestriction,
  )
  const { granularity } = usePeriod()

  const lastDateRestriction = useMemo(
    () => (lastDate ? new Date(lastDate) : maxDateRestriction),
    [lastDate, maxDateRestriction],
  )

  const bestWorstEnabled = BEST_WORST_ENABLED || params.enable_best_worst

  const renderConfig = useMemo(
    () => ({
      currentCompany,
      ...(renderConfigOverride ?? {}),
    }),
    [currentCompany, renderConfigOverride],
  )

  const visibleTabs: TBrandMediaSocialPostsDashboardCharts = useMemo(
    () =>
      Object.keys(items)
        .filter(chartId => {
          const { requiredSources } =
            items[chartId as unknown as TBrandMediaSocialPostsTableId]
          return (
            !requiredSources?.length ||
            requiredSources?.some((source: string) => sources?.has(source))
          )
        })
        .reduce(
          (filteredCharts, chartId) => {
            filteredCharts[
              chartId as unknown as TBrandMediaSocialPostsTableId
            ] = items[chartId as unknown as TBrandMediaSocialPostsTableId]
            return filteredCharts
          },
          {} as Record<
            TBrandMediaSocialPostsTableId,
            TBrandMediaSocialPostsTable
          >,
        ),
    [items],
  )

  const defaultVisibleChart = useMemo(
    () =>
      visibleTabs[
        Object.keys(visibleTabs)[0] as unknown as TBrandMediaSocialPostsTableId
      ],
    [visibleTabs],
  )
  const widgetURLSearchParam =
    params[URL_WIDGET_PARAM_KEY] ?? defaultVisibleChart?.id

  const activeWidget = useMemo(() => {
    const widgetId =
      widgetURLSearchParam?.toUpperCase() as TBrandMediaSocialPostsTableId

    if (widgetURLSearchParam && visibleTabs[widgetId]) {
      return visibleTabs[widgetId]
    }
    return defaultVisibleChart
  }, [widgetURLSearchParam])

  const chartProps = useMemo(() => {
    const props = VISUALIZATIONS_MAP[activeWidget?.widget]?.props
    return {
      ...props,
      renderConfig,
      queryPrefix: props?.queryPrefix,
      sortBy: props?.sortBy,
      columnConfig: props?.columnConfig,
      highlightCells: props?.highlightCells,
    }
  }, [activeWidget, renderConfig])

  const defaultColumnKeys = useMemo(() => {
    const props = VISUALIZATIONS_MAP[activeWidget?.widget]?.props
    return props?.columnConfig.reduce(
      (
        acc: string[],
        column: TableVisualizationColumn & { disabled: boolean },
      ) => {
        if (!column.disabled) {
          acc.push(column.key)
        }
        return acc
      },
      [],
    )
  }, [activeWidget])

  const queryPrefix = chartProps?.queryPrefix

  const chartPropsSorting = useMemo(() => {
    // `defaultSortBy` lets the caller pick a non-default sort column without
    // touching the shared widget config in charts.tsx.
    const sortKey = defaultSortBy ?? chartProps?.sortBy
    return sortKey ? [{ id: sortKey, desc: true }] : []
  }, [chartProps, defaultSortBy])

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    activeWidget?.widget,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = (chartProps?.columnConfig ?? []).map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
          icon: column.isBrand ? <MarathonDataLogo /> : null,
        }) as TMultiSelectOption,
    )

    return options || []
  }, [chartProps])

  const cubeFilters: TCubeFilterOptions = useMemo(() => {
    const filters: Filter[] = convertToCubeFilters(queryPrefix, {
      dep_var: depVarConfig.depVar,
      dep_var_scaler: depVarConfig.depVarScaler,
    })

    // Channel-agnostic widgets (e.g. OrganicSocialPosts' "ALL_POSTS" or the
    // Ads widget) don't carry a mediaChannel — skip the channel filter for
    // them. The dashboard is reusable for any widget that fits this shape.
    if (!activeWidget?.mediaChannel) {
      return {
        and: [...filters, ...(extraCubeFilters ?? [])],
      }
    }

    const mediaChannel = activeWidget?.mediaChannel

    return {
      and: [
        ...filters,
        {
          dimension: `${queryPrefix}.media_channel`,
          operator: "equals" as BinaryOperator,
          values: [mediaChannel || ""],
        },
        ...(extraCubeFilters ?? []),
      ],
    }
  }, [queryPrefix, activeWidget, depVarConfig, extraCubeFilters])

  const columnConfigFiltered = useMemo(
    () =>
      (chartProps?.columnConfig ?? []).filter(({ key }: { key: string }) =>
        (columnKeys as string[])?.includes(key),
      ),
    [chartProps?.columnConfig, columnKeys],
  )

  const renderChannelIcon = (tab: TBrandMediaSocialPostsTable) => {
    if (!tab.mediaChannel) {
      return null
    }

    const source = sources.get(tab?.mediaChannel.toUpperCase())
    return (
      <>
        {renderSourceIcon({
          // name: source?.name,
          icon: source?.icon,
        } as TSource)}
      </>
    )
  }

  const changePeriod = ({ start, end }: { start: Date; end: Date }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )
  }

  const handleSetWidget = (widgetId: string) => {
    setParams({
      [URL_WIDGET_PARAM_KEY]: widgetId.toLowerCase(),
    })
  }

  const handleOpenDrawer = (config: Partial<TDrawerConfig>) => {
    const drawerType = DRAWER_TYPE.CampaingAdSetAdDetails
    const component = drawerComponent

    openDrawer({
      ...config,
      props: {
        ...config.props,
        component,
        widget: activeWidget?.widget,
        prefix: queryPrefix,
      },
      type: drawerType,
    } as TDrawerConfig)
  }

  const renderTable = () => (
    <div className="mt-3">
      <Chart
        {...chartProps}
        highlightCells={chartProps?.highlightCells && highlight}
        chartId={activeWidget?.widget}
        className="min-h-120"
        summaryWidget={activeWidget?.summaryWidget}
        widgetType={VISUALIZATION_TYPE.TableVisualization}
        component={TableVisualization}
        dateRange={dateRange}
        compareRange={compareRange ?? undefined}
        granularity={granularity}
        sources={sources}
        defaultSorting={chartPropsSorting}
        useOrderQueryPrefix={useOrderQueryPrefix}
        onRowClick={onRowClick}
        requiredSources={activeWidget?.requiredSources}
        hideAggSelector={true}
        setDataAggType={() => undefined}
        cubeFilters={cubeFilters}
        useLocalStoragePagination={!activeWidget?.summaryWidget}
        // Leading columns (typically a row-select checkbox, and sometimes a
        // creative preview) are prepended outside the column-picker's
        // visibility set so users can't accidentally hide them.
        columnConfig={[
          ...(leadingColumns ?? []),
          ...(chartProps?.columnConfig ?? []).filter(
            ({ key }: { key: string }) =>
              (columnKeys as string[])?.includes(key),
          ),
        ]}
        enableCSVExport={true}
        csvExportFilename={activeWidget?.label || "social-posts"}
        openDrawer={handleOpenDrawer}
      />
    </div>
  )

  return (
    <>
      {!hideBreadcrumb && (
        <div className="flex items-center justify-between gap-system-xs">
          <DashboardBreadcrumbs slug={slug} title={title} parent={parent} />
        </div>
      )}
      {bestWorstEnabled && (
        <div className="mt-3">
          <BrandMediaSocialPostsSortedSection
            activeWidget={
              visibleTabs[
                BRAND_MEDIA_SOCIAL_POSTS_TABLES.ALL_POSTS as TBrandMediaSocialPostsTableId
              ]
            }
            dateRange={dateRange}
          />
        </div>
      )}
      <div className="flex justify-between items-stretch gap-system-xs mt-system-lg mb-system-xl">
        {hideChannelTabs ? (
          // Spacer so the right-side toolbar (filters, date range, columns)
          // stays right-aligned when the channel strip is suppressed.
          <div />
        ) : (
          <div className="join">
            {Object.values(visibleTabs).map(tab => (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                key={tab.id}
                title={tab.label}
                size="sm"
                className={cn(
                  `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm  rounded-system-md`,
                  { "bg-system-blue-800": tab.id !== activeWidget?.id },
                  {
                    "bg-system-blue-600": tab.id === activeWidget?.id,
                  },
                )}
                onClick={() => handleSetWidget(tab.id)}
              >
                {renderChannelIcon(tab)}
                <span
                  className={cn("font-[Lato] ", {
                    "text-base text-system-gray-300 font-normal":
                      tab.id !== activeWidget?.id,
                    "text-white font-semibold text-base":
                      tab.id === activeWidget?.id,
                  })}
                >
                  {tab.label}
                </span>
              </Button>
            ))}
          </div>
        )}
        <div className="flex justify-end items-stretch gap-system-sm mb-system-lg">
          {chartProps?.highlightCells && (
            <HighlightToggle
              isEnabled={highlight}
              toggle={() => setHighlight((val: boolean) => !val)}
            />
          )}
          {toggleAmazon && (
            <AmazonToggle
              amazonEnabled={amazonEnabled}
              toggleAmazon={toggleAmazon}
            />
          )}
          <MultiSelect
            label="Columns"
            options={columnOptions as TMultiSelectOption[]}
            value={columnOptions.filter((option: TMultiSelectOption) =>
              (columnKeys as string[]).includes(option.value),
            )}
            onChange={(options: TMultiSelectOption[]) =>
              setColumnKeys(options.map(({ value }) => value))
            }
            className="text-sm"
          />
          <TableFiltersBar
            id={activeWidget?.widget}
            columnConfig={columnConfigFiltered}
          />
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                comparisonEnabled={false}
                maxDate={lastDateRestriction}
                onChange={changePeriod}
              />
            )}
          <RichTooltip
            content={
              <LastUpdatedChip
                usedInTooltip={true}
                lastDate={lastDate}
                isLoading={isLastUpdateDateLoading}
              >
                Max date available in Page:
              </LastUpdatedChip>
            }
          >
            <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
              <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
                <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
              </span>
              <InformationCircleIcon className="w-system-lg h-system-lg" />
            </Card>
          </RichTooltip>
        </div>
      </div>
      <div className="mt-3">
        {activeWidget ? (
          renderTable()
        ) : (
          <div>
            It seems like you don’t have active connection to show anything on
            this page. Please{" "}
            <Link
              className="link"
              to="/setup/connectors"
              title="Connector page"
            >
              review your connections.
            </Link>
          </div>
        )}
      </div>
    </>
  )
}
